/**
 * Typed API client for Ballast Payroll.
 *
 * Wraps fetch with:
 *  - Base URL from VITE_API_URL (defaults to "" so Vite dev proxy handles /api/*)
 *  - Authorization: Bearer <token> header when a token is supplied
 *  - JSON Content-Type on mutating requests
 *  - Unwrapping of the { success, data, error, message } envelope
 *
 * @module api/client
 */

import type { ApiResponse } from '../types';

/** The result type returned by apiRequest. */
export type ApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: string; message: string };

/** Exported so AuthContext's two direct fetches target the same API origin (P3-16). */
export const BASE_URL = (import.meta.env['VITE_API_URL'] as string | undefined) ?? '';

/**
 * 401 codes that warrant a silent refresh-and-retry (review 2026-08-08 P3-16).
 * TOKEN_EXPIRED is the ordinary ~1h expiry. INVALID_TOKEN is what every
 * in-memory access token becomes after a JWT-secret rotation (done twice in
 * prod already): the refresh cookie mints a valid replacement, so treating it
 * as refreshable turns "every click errors until a manual reload" into a
 * seamless recovery — and when the refresh itself fails, the normal
 * clear-session + /login bounce applies.
 */
const REFRESHABLE_401 = new Set(['TOKEN_EXPIRED', 'INVALID_TOKEN']);

/** One shared in-flight refresh so concurrent 401s don't stampede the endpoint. */
let refreshInFlight: Promise<string | null> | null = null;

/**
 * Requests a fresh access token using the httpOnly refresh cookie, which the browser sends
 * automatically (credentials: 'include'). The refresh token is rotated server-side and is
 * never exposed to JS — so there is nothing in localStorage for an XSS to steal.
 *
 * On success: broadcasts the new access token via the 'sbp:token' window event (AuthContext
 * updates its in-memory state) and returns it. On failure: broadcasts 'sbp:logout' so
 * AuthContext clears the session, and returns null. (Design review 2026-07-15, P0-1.)
 */
export async function refreshAccessToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async (): Promise<string | null> => {
    // Review 2026-08-08 P1: the in-flight dedupe above is per-tab only, but the
    // refresh COOKIE is shared — two tabs bootstrapping together (session
    // restore, cmd-click) present the same rotated token concurrently, and the
    // server's reuse detection revokes the whole family. The Web Locks API
    // serializes refreshes across tabs; each sequential refresh then rotates
    // validly. Browsers without navigator.locks just run unserialized (the old
    // behavior).
    const doRefresh = async (): Promise<string | null> => {
      try {
        const res = await fetch(`${BASE_URL}/api/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
        });
        const json = (await res.json()) as ApiResponse<{ accessToken: string }>;
        if (res.ok && json.success && json.data?.accessToken) {
          const fresh = json.data.accessToken;
          window.dispatchEvent(new CustomEvent('sbp:token', { detail: fresh }));
          return fresh;
        }
      } catch {
        // network error — fall through to the logout signal
      }
      window.dispatchEvent(new Event('sbp:logout'));
      return null;
    };
    if (typeof navigator !== 'undefined' && navigator.locks?.request) {
      return navigator.locks.request('ballast:refresh', doRefresh);
    }
    return doRefresh();
  })();
  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
}

/**
 * Makes an authenticated HTTP request and unwraps the ApiResponse envelope.
 *
 * @param path    - API path, e.g. "/api/employees"
 * @param token   - JWT access token; omit or pass null for unauthenticated requests
 * @param options - Standard RequestInit (method, body, etc.)
 * @returns       ApiResult<T> — either { ok: true, data } or { ok: false, error, message }
 *
 * @example
 *   const result = await apiRequest<EmployeeListData>('/api/employees', token);
 *   if (result.ok) { console.log(result.data.employees); }
 */
export async function apiRequest<T>(
  path: string,
  token: string | null,
  options: RequestInit = {},
  /** Internal: set once we've already refreshed + retried, to avoid a loop. */
  retried = false,
): Promise<ApiResult<T>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const res = await fetch(`${BASE_URL}${path}`, {
      credentials: 'include',
      ...options,
      headers: {
        ...headers,
        ...(options.headers as Record<string, string> | undefined),
      },
    });

    // Parse defensively. A restarting backend or a gateway error can return an
    // empty or non-JSON body; calling res.json() on that throws "Unexpected end of
    // JSON input", which used to surface as a confusing raw error to the user.
    const raw = await res.text();
    let json: ApiResponse<T> | null = null;
    if (raw) {
      try {
        json = JSON.parse(raw) as ApiResponse<T>;
      } catch {
        json = null; // non-JSON body (HTML error page, etc.)
      }
    }
    if (json === null) {
      return {
        ok: false,
        error: 'SERVER_UNAVAILABLE',
        message: `The server returned an unexpected response (HTTP ${res.status}). Please try again in a moment.`,
      };
    }

    if (json.success && json.data !== undefined) {
      return { ok: true, data: json.data };
    }

    // Access token expired (or invalidated by a key rotation) mid-session →
    // silently refresh and retry once. On a failed refresh,
    // refreshAccessToken() has cleared the session; bounce to login.
    if (!retried && token && json.error && REFRESHABLE_401.has(json.error)) {
      const fresh = await refreshAccessToken();
      if (fresh) return apiRequest<T>(path, fresh, options, true);
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }

    return {
      ok: false,
      error: json.error ?? 'UNKNOWN_ERROR',
      message: json.message ?? 'An unexpected error occurred.',
    };
  } catch (err) {
    const message =
      err instanceof Error ? err.message : 'Network error. Please try again.';
    return { ok: false, error: 'NETWORK_ERROR', message };
  }
}

/** The result type returned by apiDownload. */
export type DownloadResult =
  | { ok: true }
  | { ok: false; error: string; message: string };

/**
 * Fetches a binary/text endpoint (PDF, EFW2 file) with auth and triggers a
 * browser download. On failure the JSON error envelope is parsed when present.
 *
 * @param path     - API path, e.g. "/api/tax/941/pdf?year=2026&quarter=1"
 * @param token    - JWT access token
 * @param filename - Suggested filename for the browser download
 * @param options  - Standard RequestInit (method, body, etc.)
 */
export async function apiDownload(
  path: string,
  token: string | null,
  filename: string,
  options: RequestInit = {},
  /** Internal: set once we've already refreshed + retried. */
  retried = false,
): Promise<DownloadResult> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  try {
    const res = await fetch(`${BASE_URL}${path}`, {
      credentials: 'include',
      ...options,
      headers: { ...headers, ...(options.headers as Record<string, string> | undefined) },
    });

    if (!res.ok) {
      try {
        const json = (await res.json()) as ApiResponse<unknown>;
        if (!retried && token && json.error && REFRESHABLE_401.has(json.error)) {
          const fresh = await refreshAccessToken();
          if (fresh) return apiDownload(path, fresh, filename, options, true);
          if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
        return {
          ok: false,
          error: json.error ?? 'DOWNLOAD_FAILED',
          message: json.message ?? `Download failed (HTTP ${res.status}).`,
        };
      } catch {
        return { ok: false, error: 'DOWNLOAD_FAILED', message: `Download failed (HTTP ${res.status}).` };
      }
    }

    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return { ok: true };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Network error. Please try again.';
    return { ok: false, error: 'NETWORK_ERROR', message };
  }
}
